import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, EmptyState, Pill, Skeleton, Tabs, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { useDealRecord, useInvalidateDeals } from '../api';
import { HistoryPanel } from '../dialogs/HistoryPanel';
import { LoseDialog } from '../dialogs/LoseDialog';
import { ReopenDialog } from '../dialogs/ReopenDialog';
import { StageDialog } from '../dialogs/StageDialog';
import type { DealRecord, StageBarEntry } from '../types';
import { CommissionTab } from './CommissionTab';
import { ContactTab } from './ContactTab';
import { DocumentsTab } from './DocumentsTab';
import { FinancingNotice } from './FinancingNotice';
import { FinancingsTab } from './FinancingsTab';
import { HistoryTab } from './HistoryTab';
import { OverviewTab } from './OverviewTab';
import { UnitsTab } from './UnitsTab';
import styles from './record.module.css';

/**
 * Deal-Akte 01–10 (`/deals/:id?tab=`): breadcrumb, name + status pill, subline, the actions, the stage
 * bar (every stage the API returns), the tab bar with the open counts, and the tab pages.
 */

type RecordTab = 'overview' | 'contact' | 'units' | 'financings' | 'documents' | 'commission' | 'history';
const TABS: readonly RecordTab[] = [
  'overview',
  'contact',
  'units',
  'financings',
  'documents',
  'commission',
  'history',
];

function isRecordTab(value: string | null): value is RecordTab {
  return (TABS as readonly string[]).includes(value ?? '');
}

type DialogState =
  | { kind: 'stage'; targetStageId: string | null }
  | { kind: 'lose' }
  | { kind: 'reopen' }
  | { kind: 'history' };

function stageClass(stage: StageBarEntry, lost: boolean): string | undefined {
  switch (stage.state) {
    case 'done':
      return lost ? styles.stageMuted : styles.stageDone;
    case 'current':
      return styles.stageCurrent;
    case 'locked':
      return styles.stageLocked;
    case 'lost':
      return styles.stageLost;
    case 'open':
      return undefined;
  }
}

function StageBar({ deal }: { deal: DealRecord }) {
  const lost = deal.status === 'lost';
  return (
    <div
      className={styles.stages}
      role="list"
      aria-label="Stages"
      style={{ gridTemplateColumns: `repeat(${deal.stages.length}, minmax(0, 1fr))` }}
    >
      {deal.stages.map((stage) => (
        <div
          key={stage.id}
          role="listitem"
          aria-label={`${stage.name}${stage.hint ? ` · ${stage.hint}` : ''}`}
          className={clsx(styles.stage, stageClass(stage, lost))}
        >
          <span className={styles.stageBar} aria-hidden="true" />
          <span className={styles.stageName}>{stage.name}</span>
          {stage.hint ? <span className={styles.stageHint}>{stage.hint}</span> : null}
        </div>
      ))}
    </div>
  );
}

function tabLabel(label: string, count: number | null) {
  return (
    <>
      {label}
      {count !== null && count > 0 ? <span className={styles.tabBadge}>{formatInteger(count)}</span> : null}
    </>
  );
}

function RecordSkeleton() {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.top}>
        <Skeleton width={160} height={12} />
        <Skeleton width={320} height={30} />
        <Skeleton width={420} height={12} />
        <Skeleton variant="block" height={36} />
      </div>
      <div className={styles.content}>
        <div className={styles.main}>
          <Skeleton variant="block" height={140} />
          <Skeleton variant="block" height={180} />
        </div>
        <div className={styles.side}>
          <Skeleton variant="block" height={120} />
          <Skeleton variant="block" height={220} />
        </div>
      </div>
    </div>
  );
}

export function DealRecordPage() {
  const params = useParams();
  const id = params['id'] ?? '';
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidate = useInvalidateDeals();
  const [search, setSearch] = useSearchParams();
  const record = useDealRecord(id);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const tabParam = search.get('tab');
  const tab: RecordTab = isRecordTab(tabParam) ? tabParam : 'overview';
  function setTab(next: RecordTab) {
    setSearch(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'overview') params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  }

  if (record.isPending) return <RecordSkeleton />;
  if (record.isError) {
    const status = record.error instanceof ApiError ? record.error.status : null;
    return (
      <div className={styles.page}>
        <div className={styles.content} style={{ gridTemplateColumns: 'minmax(0,1fr)' }}>
          {status === 404 || status === 403 ? (
            <EmptyState
              eyebrow="Deals"
              title="Dieser Deal ist für Sie nicht sichtbar."
              description={record.error.message}
              actions={
                <Button variant="secondary" onClick={() => void navigate('/deals')}>
                  Zur Pipeline
                </Button>
              }
            />
          ) : (
            <Banner
              tone="danger"
              actions={
                <Button variant="secondary" size="small" onClick={() => void record.refetch()}>
                  Erneut laden
                </Button>
              }
            >
              {errorMessage(record.error)}
            </Banner>
          )}
        </div>
      </div>
    );
  }

  const deal = record.data;
  const lost = deal.status === 'lost';
  const cancelled = deal.status === 'cancelled';
  const writable = deal.permissions.canChangeStage && !deal.permissions.readOnly && deal.status === 'active';
  const unitNumber = deal.units[0]?.number ?? null;
  const line = deal.subline.replace(/ · Closer .*$/, '');

  const tabItems = [
    { value: 'overview' as const, label: 'Übersicht' },
    { value: 'contact' as const, label: 'Kontakt & Termine' },
    { value: 'units' as const, label: 'Einheiten' },
    {
      value: 'financings' as const,
      label: tabLabel(
        lost || deal.counts.financings === 0 ? 'Finanzierung' : 'Finanzierungen',
        lost ? null : deal.counts.financings,
      ),
    },
    { value: 'documents' as const, label: tabLabel('Dokumente', lost ? null : deal.counts.documentsOpen) },
    { value: 'commission' as const, label: 'Provision' },
    { value: 'history' as const, label: 'Verlauf' },
  ];

  function afterChange(message: string, updated: DealRecord, nextStep: 'book_appointment' | null = null) {
    setDialog(null);
    void invalidate(updated.id);
    toast({ message });
    if (nextStep === 'book_appointment') void navigate(`/contacts/${updated.contact.id}?editor=appointment`);
  }

  return (
    <div className={styles.page}>
      <div className={styles.top}>
        <div className={styles.head}>
          <div className={styles.headText}>
            <div className={styles.crumbs}>
              <button type="button" className={styles.crumbLink} onClick={() => void navigate('/deals')}>
                Deals
              </button>
              <span>/</span>
              <span>{deal.propertyName ?? `Deal #${formatInteger(deal.number)}`}</span>
            </div>
            <div className={styles.titleRow}>
              <h1 className={styles.title}>{deal.contact.name}</h1>
              <Pill variant={deal.statusTone}>{deal.statusLabel}</Pill>
            </div>
            <div className={styles.subline}>{deal.subline}</div>
          </div>
          <div className={styles.headActions}>
            {deal.permissions.readOnly && deal.permissions.readOnlyText && !lost ? (
              <span className={styles.readOnlyNote}>{deal.permissions.readOnlyText}</span>
            ) : null}
            {lost ? (
              <>
                {deal.permissions.canReopen ? (
                  <Button variant="secondary" onClick={() => setDialog({ kind: 'reopen' })}>
                    Wieder öffnen
                  </Button>
                ) : null}
                <Button variant="secondary" onClick={() => setDialog({ kind: 'history' })}>
                  Verlauf öffnen
                </Button>
              </>
            ) : null}
            {!lost && !cancelled ? (
              <>
                {deal.permissions.canLose && !deal.permissions.readOnly ? (
                  <Button variant="secondary" onClick={() => setDialog({ kind: 'lose' })}>
                    Verloren setzen
                  </Button>
                ) : null}
                {writable ? (
                  <Button onClick={() => setDialog({ kind: 'stage', targetStageId: null })}>
                    Stage wechseln
                  </Button>
                ) : null}
              </>
            ) : null}
            {cancelled ? (
              <Button variant="secondary" onClick={() => setDialog({ kind: 'history' })}>
                Verlauf öffnen
              </Button>
            ) : null}
          </div>
        </div>
        {notice ? (
          <Banner tone="warning" className={styles.readOnlyNote} style={{ display: 'block' }}>
            {notice}
          </Banner>
        ) : null}
        <FinancingNotice deal={deal} onLose={() => setDialog({ kind: 'lose' })} />
        <StageBar deal={deal} />
        <Tabs label="Deal-Akte" items={tabItems} value={tab} onChange={setTab} />
      </div>

      {tab === 'overview' ? (
        <OverviewTab
          deal={deal}
          onChangeStage={(targetStageId) => setDialog({ kind: 'stage', targetStageId })}
        />
      ) : null}
      {tab === 'contact' ? <ContactTab deal={deal} /> : null}
      {tab === 'units' ? <UnitsTab deal={deal} /> : null}
      {tab === 'financings' ? <FinancingsTab deal={deal} /> : null}
      {tab === 'documents' ? <DocumentsTab deal={deal} /> : null}
      {tab === 'commission' ? <CommissionTab deal={deal} /> : null}
      {tab === 'history' ? <HistoryTab deal={deal} /> : null}

      {dialog?.kind === 'stage' ? (
        <StageDialog
          open
          subject={{ id: deal.id, contactName: deal.contact.name, line }}
          targetStageId={dialog.targetStageId}
          onClose={() => setDialog(null)}
          onDone={(updated, nextStep) =>
            afterChange(`Stage gesetzt: ${updated.stageName}.`, updated, nextStep)
          }
        />
      ) : null}
      {dialog?.kind === 'lose' ? (
        <LoseDialog
          open
          dealId={deal.id}
          contactName={deal.contact.name}
          line={line}
          unitNumber={unitNumber}
          onClose={() => setDialog(null)}
          onDone={(updated, nextStep) => afterChange('Deal verloren gesetzt.', updated, nextStep)}
        />
      ) : null}
      {dialog?.kind === 'reopen' && deal.lost ? (
        <ReopenDialog
          open
          dealId={deal.id}
          contactName={deal.contact.name}
          line={line}
          lastStageName={deal.lost.lastStageName}
          onClose={() => setDialog(null)}
          onDone={(updated, reopenNotice) => {
            setNotice(reopenNotice);
            afterChange(`Deal wieder geöffnet: ${updated.stageName}.`, updated);
          }}
        />
      ) : null}
      {dialog?.kind === 'history' ? (
        <HistoryPanel
          open
          dealId={deal.id}
          contactName={deal.contact.name}
          line={deal.unitLine}
          onClose={() => setDialog(null)}
          onOpenDeal={() => {
            setDialog(null);
            setTab('history');
          }}
        />
      ) : null}
    </div>
  );
}
